// ─────────────────────────────────────────────────────────────────────────────
// runControl.ts — THE OFF SWITCH, PURE.
//
// OWNER RULING 2026-08-31, and it was proven on the box the minute it was made:
// WITH BUCK WALKING, PIN LOGIN TAKES 40 SECONDS. WITH HIM OFF IT IS INSTANT. The
// owner had to SSH in and run `run_buck.py --off` to get his own interface back
// — which is the exact situation this control exists to prevent, and it was not
// on screen. So the control leaves the composer and goes where he lands, and it
// is now THE ONLY CONTROL HE HAS.
//
// ── A CONTROL THAT NEEDS A HEALTHY APP TO SWITCH OFF THE THING MAKING THE APP
//    UNHEALTHY IS NOT A CONTROL ────────────────────────────────────────────
// That is the whole reason this module exists, and the defect it names was in my
// own shipped code. `ComposerAgentControls` disabled the LOOP chip until
// `GET /api/rexy/agent-loop` answered. MEASURED 2026-08-31 on an IDLE box:
//
//     loop_toggle.state('GROWTH','buck')            0.04 ms   ← the consent fact
//     the route's capability read, GROWTH/buck   2653.60 ms   ← 1607 cascade rows
//     the same read, PERSONAL/spencer               4.10 ms
//     growth_runner.status()                        2.88 ms
//
// `server/app.py` monkey-patches eventlet, so that 2.65 s is a HUB-BLOCKING read
// on the single process Buck is already saturating. The old control made the
// owner buy the most expensive read in the fleet — 66,000x the cost of the fact
// it wanted — to reach the switch that stops the thing saturating the hub. Under
// load that is exactly backwards, and it is why the switch was dead when he
// needed it.
//
// ── SO: OFF IS ALWAYS SENDABLE. ON IS NOT. ─────────────────────────────────
// The asymmetry is the ruling, and it is safe in one direction only:
//   • Sending `enabled:false` for an agent already off is a no-op at the store,
//     and the route's `warm()` and its Buck kick BOTH sit inside `if enabled`
//     (server/app.py) — so a stop starts nothing, warms nothing, spends nothing.
//     Its worst case is "an agent that was off is off", which is the direction
//     this entire mechanism already fails in.
//   • Arming without a read would be arming something we cannot describe, and
//     for Buck it STARTS A WALK. NEVER INVENT ON stands, unchanged.
//
// ── WHAT THE CLICK MAY AND MAY NOT DO — EVERY EARLIER REFUSAL SURVIVES ─────
//   never invent ON ............ an unread state renders unread, never OFF/ON
//   the click never moves the control ... `sending` is a TRANSPORT fact about
//                                 the request, never a claim about the agent
//   a failed write re-reads .... it never keeps the value that was clicked
//   one agent never paints on another ... the identity gate is upstream, in
//                                 `normalizeAgentLoop`, and in the store's key
//   the stop is never instant .. he finishes the round in flight, up to ~3.5
//                                 minutes, and is never killed mid-company
//
// ── A CONTROL EXPLAINS ITSELF IN A FEW WORDS OR NOT AT ALL ────────────────
// OWNER RULING 2026-08-31, and it SUPERSEDES the 2026-08-27 line that said a
// refusal gets its own surface rather than being shortened. The first build of
// this module rendered EIGHT LINES of justification into a navigation rail:
// why OFF was still offered, what arming would mean, what unread is not. THE
// REASONING IS CORRECT AND IT BELONGS IN THE SOURCE — it is the same fault as
// the campaign cell and the Unknown column, a producer printing its own
// justification into a display slot.
//
// So every string below is a FEW WORDS. The long form did not move to a
// tooltip and get shorter on the way; it was deleted, and what it explained is
// written here, where the next person to touch this reads it.
//
// ── THE CONTROL ANSWERS THE QUESTION. IT DOES NOT EXPLAIN ITSELF. ─────────
// OWNER RULING 2026-08-31, after clicking the control and receiving a paragraph
// about its own epistemology: "HE STILL DOES NOT KNOW WHETHER BUCK IS
// AUTONOMOUS. THE CONTROL EXPLAINED ITS OWN EPISTEMOLOGY INSTEAD OF ANSWERING
// THE QUESTION. Every sentence in it is true and correct and NONE OF IT BELONGS
// ON A SCREEN."
//
// So the whole vocabulary is now:
//
//     RUN                 he is off
//     STOP                he is walking, or armed
//     UNREAD · RETRYING   we could not read it, and we are trying again
//
// and nothing else. The sentences that used to be rendered are kept — HERE, in
// this comment, for the next builder rather than for the owner:
//
//   "Not read is not off. Nothing answered, so nothing is shown; this is not
//    'he is not working', it is 'we did not read'."
//   "Stop is still offered while unread because stopping an agent that is
//    already off does nothing — it starts nothing and spends nothing."
//   "Arming is not offered while unread, because arming something we cannot
//    read would be inventing an ON."
//
// THE THIRD ONE IS NOW OVERRULED, and it is the important part of this ruling.
// Refusing to ARM while unread meant the owner could not turn Buck on from the
// screen AT ALL — "he can only stop something he cannot see. THE ON SWITCH IS
// STILL AN SSH COMMAND." A refusal that never lifts is not caution, it is a
// missing control. So the refusal is now TEMPORARY: it holds while the read is
// retrying (1s, 2s, 4s) and then the control offers BOTH actions and says, in
// four words, that it is guessing at neither.
//
// ── AND THE LABEL IS NEVER A QUESTION MARK ────────────────────────────────
// `RUN ?` shipped and the owner read it as the bug it looks like. A chip that
// cannot state the agent's state can still state ITS OWN ACTION, and the action
// is the thing being pressed: the unread chip says STOP. The tone still says we
// did not read. For the harness agent an unread state is a FAULT rather than a
// quiet nothing — his read is 2.88 ms — and it comes back named.
//
// ── AND ONE NEW STATE, BECAUSE 40 SECONDS NEEDED ONE ──────────────────────
// SENT · UNCONFIRMED. The POST was dispatched and nothing has come back yet.
// It is NOT "off" — we have not been told that — and it is not a failure
// either. It is the honest third thing, and on a starved box it is the state the
// owner will actually be looking at. It says what was sent, that it is not
// confirmed, and that the write lands before the answer does (the route calls
// `loop_toggle.set_state` at the top of its POST branch and pays the 2.65 s
// capability read AFTERWARDS — so the stop is already on disk while this reads).
// ─────────────────────────────────────────────────────────────────────────────

import { AgentLoopState } from '../api/agentLoop';
import { RunnerStatus } from '../api/growthRunner';

/** How the read is going. `failed` and `unread` are DIFFERENT: one of them
 *  tried. Both render the same tone, because neither one knows. */
export type ReadPhase = 'unread' | 'reading' | 'read' | 'failed';

/** How the write is going. `unconfirmed` is not an error — see the header. */
export type WritePhase = 'idle' | 'sending' | 'unconfirmed';

export type RunTone =
  'walking' | 'stopping' | 'halted' | 'on' | 'off' | 'unread' | 'pending' | 'unconfirmed';

export type RunAction = 'stop' | 'arm';

export interface RunControlInput {
  /** The generic per-agent toggle read. Null when unread, or when this is the
   *  harness agent (whose state comes from `runner` instead — see below). */
  loop: AgentLoopState | null;
  /** How the GENERIC toggle read is going. NOT CONSULTED ON THE HARNESS PATH —
   *  his answer comes from `runner` alone, and R16 asserts that outright rather
   *  than leaving it as an implication nothing can convict. */
  read: ReadPhase;
  write: WritePhase;
  /** What was asked for by the in-flight write. Null when none is. */
  writeWanted: boolean | null;
  /**
   * THE HARNESS AGENT'S CHEAP READ. `/api/growth/runner` costs 2.88 ms and
   * carries `armed`, `ruled` and `state`, so BUCK'S CONTROL NEVER FIRES THE
   * 2.65-SECOND ROUTE TO RENDER ITSELF. That is not an optimisation: firing it
   * was adding a hub-blocking read to the surface whose whole job is to end
   * hub starvation.
   */
  runner: RunnerStatus | null;
  /** Does this agent have a work harness to start and stop? Buck alone, today.
   *  The other nine have none, so a "start the agent" control on them would be
   *  a promise they cannot keep — the narrowness `buckLane` already chose. */
  isHarness: boolean;
  /** Uppercase display name, for the sentences. */
  name: string;
  /** Consecutive failed reads. 0 while healthy. Drives RETRYING, and drives
   *  when waiting stops being an honest thing to show. */
  failures?: number;
  /** How many failures the caller will wait through before offering the
   *  actions blind. Defaults to the store's ladder length. */
  retriesBeforeOffering?: number;
}

export interface RunControlView {
  /** The chip's own text. Short enough for a rail row. */
  label: string;
  tone: RunTone;
  /** What pressing it would do. Null ⇒ nothing to press. */
  action: RunAction | null;
  actionable: boolean;
  /** The full sentence, never shortened to fit the chip. Null when the label is
   *  the whole fact. */
  detail: string | null;
  /** Is the agent known to be ON? `null` when we do not know — which is a third
   *  answer and never collapses to false. */
  on: boolean | null;
  /**
   * A SECOND ACTION THE CALLER MUST ALSO OFFER, or null.
   *
   * Set ONLY in the read-failed-and-retries-are-spent state. We do not know
   * whether he is running, so we cannot pick between RUN and STOP — and the
   * owner needs both, because this is exactly the state in which the screen was
   * useless to him. Both are safe to send blind: an arm on a walking agent
   * returns `already_running`, and a stop on a stopped one writes the value it
   * already had.
   */
  alsoOffers: RunAction | null;
}

/**
 * THE CAPABILITY REFUSALS, CARRIED ACROSS WORD FOR WORD.
 *
 * These are `ComposerAgentControls.loopDetail`'s sentences, moved rather than
 * rewritten. Moving the control to three surfaces MUST NOT quietly shorten what
 * it says on the one it already had — the refusals ARE the value of this control
 * and the live capture proved they work. The first draft of this module dropped
 * the `draft_only` sentence entirely and the composer's own K3 caught it.
 *
 * Only the generic agents reach this. The harness agent's sentence is
 * a line of its own, because his toggle also starts and stops a walk.
 */
function capabilityRefusal(loop: AgentLoopState | null, name: string): string | null {
  if (!loop || loop.capability === 'unknown') return null;
  if (loop.capability === 'out_of_scope') return `${name} IS NOT A DECLARED AGENT — NOTHING TO ARM.`;
  if (loop.capability === 'draft_only') return `NO DOCTRINE — THE LOOP WOULD NOT RUN FOR ${name}.`;
  // `full` needs no refusal; ON/OFF is the whole fact.
  return null;
}

/** Mirrors `agentRunStore.RETRIES_BEFORE_OFFERING`. Duplicated as a DEFAULT
 *  rather than imported because this module is pure and must not reach into the
 *  store; every real caller passes the store's own value. */
const DEFAULT_RETRIES_BEFORE_OFFERING = 3;

/** The stop sentence, ruled and never softened: he finishes the round in flight. */
export const STOP_IS_NOT_INSTANT =
  'HE FINISHES THE ROUND IN FLIGHT (~3.5 MIN), NEVER KILLED MID-COMPANY.';

/**
 * THE SENTENCE FOR THE STATE ITSELF, in the owner's own words and the log's.
 *
 * `journalctl` said exactly this and the owner had to read it in a terminal —
 * which is the whole fault. It is the STRIP's line, not the chip's: the chip
 * gets one word.
 */
export const STOPPING_LINE =
  'STOPPING · FINISHING THE ROUND IN FLIGHT · NEVER KILLED MID-COMPANY.';

/**
 * IS THE AGENT ON? Three-valued, deliberately.
 *
 * The harness agent answers from the cheap route; everyone else from the toggle
 * read. Neither one guesses: an unread route returns `null`, which the caller
 * renders as unread rather than as off.
 */
export function resolveOn(input: RunControlInput): boolean | null {
  if (input.isHarness) {
    const r = input.runner;
    if (!r || !r.reachable || r.state === 'unknown') return null;
    // `stopping` IS NOT ON. The consent is withdrawn — that is what the word
    // means — and only the round in flight is left. It is not `false` in the
    // sense the OFF branch means either, which is why the caller ranks the
    // stopping branch above this and never reaches that branch with it.
    return r.state === 'walking' || r.state === 'armed_not_walking' || r.state === 'halted';
  }
  if (input.read !== 'read' || !input.loop) return null;
  if (input.loop.capability === 'unknown') return null;
  return input.loop.enabled === true;
}

/**
 * CAN THIS AGENT BE ARMED FROM HERE? Never while unread — that is NEVER INVENT
 * ON applied to the action rather than to the paint.
 *
 * The harness agent is armable on the cheap read alone: HIS HARNESS IS THE
 * CAPABILITY. `growth_harness` exists, the supervisor starts him, and what
 * arming does is RULED, not derived from a doctrine count —
 * so the 2.65-second capability read buys the sentence nothing it does not
 * already have.
 */
export function resolveArmable(input: RunControlInput): boolean {
  if (input.isHarness) {
    const r = input.runner;
    return !!r && r.reachable && r.state !== 'unknown';
  }
  if (input.read !== 'read' || !input.loop) return false;
  return input.loop.capability === 'full' || input.loop.capability === 'draft_only';
}

/** The whole control, in one pure call. NEVER throws. */
export function resolveRunControl(input: RunControlInput): RunControlView {
  const name = (input.name || 'THIS AGENT').toUpperCase();

  // ── THE WRITE OWNS THE CONTROL WHILE IT IS IN FLIGHT ────────────────────
  // Ranked ABOVE the read because a read landing mid-write would otherwise
  // repaint the chip with the value the write is replacing — the control
  // appearing to snap back, which reads as the click having failed.
  if (input.write === 'sending') {
    return {
      label: input.writeWanted === false ? 'STOPPING …' : 'ARMING …',
      tone: 'pending',
      action: null,
      actionable: false,
      detail: input.writeWanted === false
        ? `STOPPING ${name} — ${STOP_IS_NOT_INSTANT}`
        : `ARMING ${name} — WAITING ON THE BOX.`,
      on: null,
      alsoOffers: null,
    };
  }

  if (input.write === 'unconfirmed') {
    const stopping = input.writeWanted === false;
    return {
      // NOT "OFF". Nothing has told us that. The label names what was SENT.
      label: stopping ? 'STOP SENT' : 'ARM SENT',
      tone: 'unconfirmed',
      // Pressable again — on a starved box a second press is the owner's only
      // recourse, and the write is idempotent at the store.
      action: stopping ? 'stop' : 'arm',
      actionable: true,
      detail: stopping
        ? 'SENT, NOT CONFIRMED. THE BOX WRITES BEFORE IT ANSWERS — PRESSING AGAIN IS SAFE.'
        : 'SENT, NOT CONFIRMED.',
      on: null,
      alsoOffers: null,
    };
  }

  // ── STOPPING — DISARMED, AND THE ROUND IS STILL IN FLIGHT ───────────────
  // RANKED ABOVE THE READ AND BELOW THE WRITE, and both halves of that matter.
  //
  // BELOW THE WRITE: a press in flight is still the honest thing to show, and
  // the write phase is a fact about the request rather than about him.
  //
  // ABOVE THE READ: `resolveOn` answers this state `false`, which would drop it
  // into the OFF branch and paint `RUN` — telling the owner he is stopped while
  // the walk is still going, which is the same lie in the other direction. He
  // is not on and he is not off; he is stopping, and it has its own word now.
  //
  // NO ACTION. Pressing STOP again writes a value he already has, and offering
  // RUN here would be offering to cancel a stop the owner just made — a control
  // the owner has not asked for, and one that would re-arm him mid-round.
  if (input.isHarness && input.runner?.state === 'stopping') {
    return {
      label: 'STOPPING',
      tone: 'stopping',
      action: null,
      actionable: false,
      detail: STOPPING_LINE,
      // NOT `false`. The consent is off, but rendering that as the plain OFF
      // this field means elsewhere is what `aria-pressed` would then announce,
      // and he is still working. Unknown is the honest third answer, and it is
      // the same one the write phases give for the same reason.
      on: null,
      alsoOffers: null,
    };
  }

  // ── HALTED — THE LANE IS DOWN AND HE WILL NOT RESTART HIMSELF ──────────
  // Owner ruling 2026-09-14. Consent is still on, so this is not OFF; nothing
  // is running and nothing will until he is re-armed, so it is not ARMED. The
  // action IS the re-arm: the runner clears the halt on the way ON and starts
  // him. STOP stays on offer for the owner who wants him off instead.
  if (input.isHarness && input.runner?.state === 'halted') {
    const down = (input.runner.halt?.down || 'a lane').toUpperCase();
    return {
      label: 'HALTED · RETRY',
      tone: 'halted',
      action: 'arm',
      actionable: true,
      detail: `${down} DOWN.`,
      on: true,
      alsoOffers: 'stop',
    };
  }

  const on = resolveOn(input);
  const armable = resolveArmable(input);

  // ── UNREAD — AND THIS IS THE STATE THE WHOLE BUILD IS FOR ───────────────
  // The owner is looking at a surface that could not read. The old control was
  // DISABLED here. It now offers the one action that is safe without knowing:
  // STOP. It still refuses to say ON or OFF, because it was not told either.
  if (on === null) {
    const spent = (input.failures ?? 0)
      >= (input.retriesBeforeOffering ?? DEFAULT_RETRIES_BEFORE_OFFERING);
    if (!spent) {
      return {
        // FOUR WORDS. The state is unread and we are trying again — that is the
        // whole of what the owner needs while it resolves itself.
        label: 'UNREAD · RETRYING',
        tone: 'unread',
        action: null,
        actionable: false,
        detail: 'READING AGAIN.',
        on: null,
        alsoOffers: null,
      };
    }
    // RETRIES SPENT. Waiting is no longer honest and refusing is no longer
    // temporary, so both actions are offered and neither state is claimed.
    return {
      label: 'RUN',
      tone: 'unread',
      action: 'arm',
      actionable: true,
      alsoOffers: 'stop',
      detail: 'STATE UNREAD — BOTH OFFERED.',
      on: null,
    };
  }

  if (on) {
    // The harness agent distinguishes ARMED from WALKING; nobody else can, and
    // nobody else pretends to.
    const walking = input.isHarness && input.runner?.state === 'walking';
    return {
      // ONE WORD. WALKING and ARMED are told apart by TONE and by the dot, not
      // by a longer label — the rail has one line and the header bar has none
      // to spare.
      label: input.isHarness ? 'STOP' : 'LOOP ON',
      tone: walking ? 'walking' : 'on',
      action: 'stop',
      actionable: true,
      detail: input.isHarness
        ? `STOPPING HIM — ${STOP_IS_NOT_INSTANT}`
        : capabilityRefusal(input.loop, name),
      on: true,
      alsoOffers: null,
    };
  }

  // ── OFF, AND WE WERE TOLD SO ────────────────────────────────────────────
  // There is nothing to stop, so no stop is offered. A permanently-pressable
  // STOP would be a control whose press means nothing half the time.
  return {
    label: input.isHarness ? 'RUN' : 'LOOP OFF',
    tone: 'off',
    action: armable ? 'arm' : null,
    actionable: armable,
    detail: input.isHarness
      ? 'ARMING HIM STARTS HIS LANE — A CHECK EVERY 60s, SO NOT INSTANT.'
      : capabilityRefusal(input.loop, name),
    on: false,
    alsoOffers: null,
  };
}
